import { create } from 'zustand';
import { ColumnState } from 'ag-grid-community';
import { defaultTQC2070_R01Dto, TQC2070_R01Dto } from './Dto_TQC2070_R01';
import { normalizeValueIndex } from './Utils_TQC2070_R01';

export interface TQC2070_R01Filter {
	searchAnalysisDt: string;    // 분석일자 (연월 형식: yyyy-MM)
	listLength?: number;          // 조회 개수 제한
	refetchListLength?: number;   // Refetch 조회 개수 제한
}

export interface Store_TQC2070_R01 {
	// === 필터 관련 ===
	TQC2070_R01Filter: TQC2070_R01Filter;
	TQC2070_R01PastFilter: TQC2070_R01Filter;
	setTQC2070_R01Filter: (filter: Partial<TQC2070_R01Filter>) => void;
	setTQC2070_R01PastFilter: (filter: Partial<TQC2070_R01Filter>) => void;

	// === 리스트 관련 ===
	TQC2070_R01List: TQC2070_R01Dto[];
	setTQC2070_R01List: (list: TQC2070_R01Dto[]) => void;
	insertTQC2070_R01List: (data: TQC2070_R01Dto[], index?: number) => void;
	removeTQC2070_R01List: (valueIndex: number) => void;
	updateTQC2070_R01List: (valueIndex: number, data: TQC2070_R01Dto) => void;
	originalTQC2070_R01List: TQC2070_R01Dto[];
	setOriginalTQC2070_R01List: (list: TQC2070_R01Dto[]) => void;
	maxValueIndex: number; // 최대 valueIndex 캐싱 (성능 최적화)

	// === 선택 관련 ===
	selectedTQC2070_R01List: number[];
	setSelectedTQC2070_R01List: (idList: number[]) => void;
	addSelectedTQC2070_R01List: (idList: number[]) => void;
	removeSelectedTQC2070_R01List: (idList?: number[]) => void;
	initSelectedTQC2070_R01List: () => void;
	allSelectedTQC2070_R01List: () => void;

	// === 삭제 관련 ===
	deletedTQC2070_R01List: number[];
	addDeletedTQC2070_R01List: (idList: number[]) => void;
	removeDeletedTQC2070_R01List: (idList?: number[]) => void;

	// === 편집 관련 ===
	editTQC2070_R01: TQC2070_R01Dto | null;
	setEditTQC2070_R01: (item: TQC2070_R01Dto | null) => void;

	// === AG Grid 컬럼 상태 관리 ===
	columnState: ColumnState[] | null;
	setColumnState: (state: ColumnState[] | null) => void;

	allCount: number;
	setAllCount: (allCount: number) => void;

	scrollTop: number | null;
	setScrollTop: (top: number | null) => void;

	// === 전체 초기화 ===
	initData: () => void;
}

// 현재 월을 yyyy-MM 형식으로 반환
const getCurrentMonth = (): string => {
	const now = new Date();
	const year = now.getFullYear();
	const month = String(now.getMonth() + 1).padStart(2, '0');
	return `${year}-${month}`;
};

export const defaultTQC2070_R01Filter: TQC2070_R01Filter = {
	searchAnalysisDt: getCurrentMonth(),
	listLength: undefined,
	refetchListLength: undefined,
};

/**
 * Zustand 스토어 정의
 */
export const useTQC2070_R01Store = create<Store_TQC2070_R01>((set) => {
	/**
	 * 공통 필터 설정 함수 생성
	 */
	const createFilterSetter = (filterKey: keyof Pick<Store_TQC2070_R01, 'TQC2070_R01Filter' | 'TQC2070_R01PastFilter'>) =>
		(filter: Partial<TQC2070_R01Filter>) => set((state) => ({
			...state,
			[filterKey]: { ...state[filterKey], ...filter }
		}));

	return {
		// === 필터 관련 ===
		TQC2070_R01Filter: defaultTQC2070_R01Filter,
		TQC2070_R01PastFilter: defaultTQC2070_R01Filter,
		setTQC2070_R01Filter: createFilterSetter('TQC2070_R01Filter'),
		setTQC2070_R01PastFilter: createFilterSetter('TQC2070_R01PastFilter'),

		// === 리스트 관련 ===
		TQC2070_R01List: [],
		setTQC2070_R01List: (list: TQC2070_R01Dto[]) => set((state) => {
			const newList: TQC2070_R01Dto[] = [];
			let fallbackIndex = 1;
			let maxValueIndex = 0; // 최대값 추적

			list.forEach((item) => {
				// 유틸 함수로 정규화
				const normalizedValueIndex = normalizeValueIndex(item.valueIndex, fallbackIndex);

				// 최대값 업데이트
				if (normalizedValueIndex > maxValueIndex) {
					maxValueIndex = normalizedValueIndex;
				}

				newList.push({
					...defaultTQC2070_R01Dto,
					...item,
					valueIndex: normalizedValueIndex,
					orderIndex: item.orderIndex > -1 ? item.orderIndex : fallbackIndex,
					orderIndexSeq: item.orderIndexSeq > 0 ? item.orderIndexSeq : 0,
				});
				fallbackIndex++;
			});

			return { 
				...state, 
				TQC2070_R01List: newList,
				maxValueIndex: maxValueIndex // 최대값 캐싱
			};
		}),
	insertTQC2070_R01List: (data: TQC2070_R01Dto[], index?: number) => set((state) => {
		const currentList = [...state.TQC2070_R01List];
		let maxValueIndex = state.maxValueIndex + 1;

		// InsertRowHandler는 rowData.valueIndex를 전달함
		// 해당 valueIndex를 가진 행의 배열 인덱스를 찾음
		const targetIndex = index !== undefined 
			? currentList.findIndex(item => item.valueIndex === index)
			: -1;

		// 실제 삽입 위치: 찾은 행의 다음 위치
		const insertPosition = targetIndex >= 0 ? targetIndex + 1 : currentList.length;
		
		// 클릭한 행의 orderIndex를 가져옴 (정렬 유지를 위해)
		const targetOrderIndex = targetIndex >= 0 ? currentList[targetIndex].orderIndex : insertPosition + 1;

		// 새 행 생성
		const preparedData = data.map((item, idx) => {
			// 유틸 함수로 정규화 (maxValueIndex를 fallback으로 사용)
			const valueIndex = normalizeValueIndex(item.valueIndex, maxValueIndex++);

			return {
				...defaultTQC2070_R01Dto,
				...item,
				valueIndex,
				orderIndex: targetOrderIndex,  // PRD1010_R01과 동일: 항상 클릭한 행의 orderIndex 사용
				orderIndexSeq: 0,              // TQC2070_R01은 단순 리스트이므로 0으로 고정
				inserted: item.inserted ?? true,
			};
		});

		// 배열의 실제 인덱스 위치에 삽입
		currentList.splice(insertPosition, 0, ...preparedData);

		return { 
			...state, 
			TQC2070_R01List: currentList,
			maxValueIndex: maxValueIndex - 1 // 마지막 사용된 값으로 업데이트
		};
	}),
		removeTQC2070_R01List: (valueIndex: number) => set((state) => ({
			...state,
			TQC2070_R01List: state.TQC2070_R01List.filter((item) => item.valueIndex !== valueIndex),
		})),
		updateTQC2070_R01List: (valueIndex: number, data: TQC2070_R01Dto) => set((state) => {
			const _list = state.TQC2070_R01List.map(item =>
				item.valueIndex === valueIndex ? data : item
			);
			return { ...state, TQC2070_R01List: _list };
		}),
		originalTQC2070_R01List: [],
		setOriginalTQC2070_R01List: (list: TQC2070_R01Dto[]) => set({ originalTQC2070_R01List: list }),

		// === 선택 관련 ===
		selectedTQC2070_R01List: [],
		setSelectedTQC2070_R01List: (idList: number[]) => set({ selectedTQC2070_R01List: idList }),
		addSelectedTQC2070_R01List: (idList: number[]) => set((state) => {
			const newSet = new Set([...state.selectedTQC2070_R01List, ...idList]);
			return {
				...state,
				selectedTQC2070_R01List: Array.from(newSet)
			};
		}),
		removeSelectedTQC2070_R01List: (idList?: number[]) => set((state) => {
			if (idList) {
				return {
					...state,
					selectedTQC2070_R01List: state.selectedTQC2070_R01List.filter(id => !idList.includes(id))
				};
			}
			return { ...state, selectedTQC2070_R01List: [] };
		}),
		initSelectedTQC2070_R01List: () => set({ selectedTQC2070_R01List: [], editTQC2070_R01: defaultTQC2070_R01Dto }),
		allSelectedTQC2070_R01List: () => set((state) => ({
			...state,
			selectedTQC2070_R01List: state.TQC2070_R01List.map(item => item.valueIndex)
		})),

		// === 삭제 관련 ===
		deletedTQC2070_R01List: [],
		addDeletedTQC2070_R01List: (idList: number[]) => set((state) => {
			// valueIndex 기준으로 삭제 표시 (숫자로 저장)
			const newSet = new Set([...state.deletedTQC2070_R01List, ...idList]);
			return {
				...state,
				deletedTQC2070_R01List: Array.from(newSet)
			};
		}),
		removeDeletedTQC2070_R01List: (idList?: number[]) => set((state) => {
			if (idList) {
				return {
					...state,
					deletedTQC2070_R01List: state.deletedTQC2070_R01List.filter(id => !idList.includes(id))
				};
			}
			return { ...state, deletedTQC2070_R01List: [] };
		}),

		// === 편집 관련 ===
		editTQC2070_R01: defaultTQC2070_R01Dto,
		setEditTQC2070_R01: (item: TQC2070_R01Dto | null) => set((state) => {
			let editData = defaultTQC2070_R01Dto;
			if (item) {
				editData = { ...item };
			}
			return { editTQC2070_R01: editData };
		}),

		// === AG Grid 컬럼 상태 관리 ===
		columnState: null,
		setColumnState: (state) => set({ columnState: state }),

		allCount: 0,
		setAllCount: (allCount: number) => set({ allCount }),

		scrollTop: null,
		setScrollTop: (top: number | null) => set({ scrollTop: top }),

		// === 전체 초기화 ===
		initData: () => set(() => ({
			TQC2070_R01Filter: defaultTQC2070_R01Filter,
			TQC2070_R01PastFilter: defaultTQC2070_R01Filter,
			TQC2070_R01List: [],
			originalTQC2070_R01List: [],
			selectedTQC2070_R01List: [],
			editTQC2070_R01: defaultTQC2070_R01Dto,
			deletedTQC2070_R01List: [],
			columnState: null,
			allCount: 0,
			maxValueIndex: 0,
			scrollTop: null,
		})),
		
		// === 최대값 캐싱 ===
		maxValueIndex: 0,
	};
});

export interface Store_TQC2070_R01_GridCalendarState {
	// ====== TQC2070_R01 GridCalendar 상태 ======
	TQC2070_R01GridCalendarState: {
		pickerVisible: boolean;
		pickerPosition: { top: number; left: number };
		targetRowIndex: number | null;
		targetField: string | null;
		targetDate: Date | undefined;
	};
	setTQC2070_R01GridCalendarState: (state: Partial<Store_TQC2070_R01_GridCalendarState['TQC2070_R01GridCalendarState']>) => void;
	initTQC2070_R01GridCalendarState: () => void;
}

export const useTQC2070_R01Store_GridCalendar = create<Store_TQC2070_R01_GridCalendarState>((set) => ({
	// ====== GridCalendar 상태 ======
	TQC2070_R01GridCalendarState: {
		pickerVisible: false,
		pickerPosition: { top: 0, left: 0 },
		targetRowIndex: null,
		targetField: null,
		targetDate: undefined
	},
	setTQC2070_R01GridCalendarState: (newState: Partial<Store_TQC2070_R01_GridCalendarState['TQC2070_R01GridCalendarState']>) => set((state) => ({
		...state,
		TQC2070_R01GridCalendarState: { ...state.TQC2070_R01GridCalendarState, ...newState }
	})),
	initTQC2070_R01GridCalendarState: () => set((state) => ({
		...state,
		TQC2070_R01GridCalendarState: {
			pickerVisible: false,
			pickerPosition: { top: 0, left: 0 },
			targetRowIndex: null,
			targetField: null,
			targetDate: undefined
		}
	})),
}));

